import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const serif = { fontFamily: "'Cormorant Garamond','EB Garamond',Georgia,serif" };
const pad3 = (n) => `#${String(n).padStart(3, "0")}`;

/**
 * FoundersWall — "Founders Wall of Honor" homepage section.
 *
 * The first 100 Founding Members receive a permanent number. Privacy first:
 * the public wall only ever shows "First name + surname initial" and only
 * for members who explicitly opted in. The counter counts ALL eligible
 * Founding Members, regardless of their visibility choice.
 * Live counter: fetched on mount and refreshed every 60s.
 */
export default function FoundersWall() {
  const { t } = useT();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/founders/wall`);
        if (!res.ok) return;
        const json = await res.json();
        if (alive) setData(json);
      } catch (_) { /* silent — wall simply keeps last state */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const count = data?.count ?? 0;
  const limit = data?.limit ?? 100;
  const remaining = data?.remaining ?? Math.max(0, 100 - count);
  const members = data?.members ?? [];

  return (
    <section
      id="founders-wall"
      data-testid="founders-wall-section"
      className="max-w-5xl mx-auto px-6 md:px-10 pt-16 md:pt-20"
    >
      <div className="relative border border-gold/30 overflow-hidden bg-[#070604]">
        {/* marble backdrop */}
        <img
          src="/textures/founders-marble.jpg"
          alt=""
          aria-hidden
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070604]/80 via-[#070604]/60 to-[#070604]/90" aria-hidden />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/70 to-transparent" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" aria-hidden />

        <div className="relative px-6 py-14 md:px-14 md:py-16 lg:px-20 lg:py-20 text-center">
          {/* Introduction */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-10 bg-gold/60" aria-hidden />
            <p className="text-[10px] tracking-[0.36em] uppercase text-gold" data-testid="founders-eyebrow">
              {t("fw.eyebrow")}
            </p>
            <span className="h-px w-10 bg-gold/60" aria-hidden />
          </div>
          <h2 style={serif} className="text-4xl sm:text-5xl text-bone leading-[1.05] tracking-tight" data-testid="founders-title">
            {t("fw.title1")} <span className="italic text-gold">{t("fw.title2")}</span>
          </h2>
          <p style={serif} className="mt-5 text-lg md:text-xl italic text-gold-soft max-w-2xl mx-auto leading-snug" data-testid="founders-subtitle">
            {t("fw.subtitle")}
          </p>
          <div className="mt-8 max-w-2xl mx-auto space-y-3 text-bone/75 font-light leading-[1.85]" data-testid="founders-body">
            <p>{t("fw.body1")}</p>
            <p>{t("fw.body2")}</p>
            <p>{t("fw.body3")}</p>
          </div>

          {/* Live counter */}
          <div className="mt-12" data-testid="founders-counter">
            <p style={serif} className="text-6xl md:text-7xl text-gold leading-none tracking-tight">
              {count}<span className="text-bone/40 mx-2">/</span>{limit}
            </p>
            <p className="mt-3 text-[11px] tracking-[0.32em] uppercase text-bone/60">
              {t("fw.counterLabel")}
            </p>
            <p className="mt-1 text-[10px] tracking-[0.22em] uppercase text-gold/60" data-testid="founders-remaining">
              {t("fw.remaining", { n: remaining })}
            </p>
          </div>

          {/* The Wall */}
          <div className="mt-12">
            {members.length > 0 ? (
              <ul
                data-testid="founders-wall-list"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-w-3xl mx-auto"
              >
                {members.map((m) => (
                  <li
                    key={m.number}
                    data-testid={`founders-member-${m.number}`}
                    className="border border-gold/20 bg-black/40 backdrop-blur-[2px] px-3 py-4 hover:border-gold/50 transition-colors"
                  >
                    <p className="text-[10px] tracking-[0.28em] text-gold/80">{pad3(m.number)}</p>
                    <p style={serif} className="mt-1.5 text-lg text-bone leading-tight">{m.display_name}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={serif} className="italic text-bone/45 text-lg" data-testid="founders-empty">
                {t("fw.empty")}
              </p>
            )}
          </div>

          {/* Call to action */}
          <div className="mt-14 mx-auto h-px w-16 bg-gold/40" aria-hidden />
          <h3 style={serif} className="mt-10 text-2xl md:text-3xl text-bone tracking-tight" data-testid="founders-cta-heading">
            {t("fw.cta.heading")}
          </h3>
          <ul className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-bone/75 font-light" data-testid="founders-cta-benefits">
            {["b1", "b2", "b3", "b4"].map((b) => (
              <li key={b} className="inline-flex items-center gap-2">
                <Check size={14} strokeWidth={2} className="text-gold shrink-0" aria-hidden />
                {t(`fw.cta.${b}`)}
              </li>
            ))}
          </ul>
          <Link
            to="/my-stoicos"
            data-testid="founders-cta-button"
            className="mt-9 inline-block min-h-[48px] px-8 py-4 bg-gold text-ink text-[11px] tracking-[0.26em] uppercase font-semibold hover:bg-gold/90 transition-colors"
          >
            {t("fw.cta.button")}
          </Link>

          {/* A Word of Gratitude — founder's closing note (not part of the numbering) */}
          <div className="mt-16 pt-12 border-t border-gold/20 max-w-2xl mx-auto" data-testid="founders-gratitude">
            <p className="text-[10px] tracking-[0.36em] uppercase text-gold/80">A Word of Gratitude</p>
            <p style={serif} className="mt-6 text-lg md:text-xl italic text-bone/70 leading-[1.9]">
              To every Founding Member who believed in StoicOs from the beginning: thank you.
            </p>
            <p style={serif} className="mt-4 text-lg md:text-xl italic text-bone/70 leading-[1.9]">
              Your trust, support, honest feedback, and willingness to walk this path with us
              have helped turn an idea into something real.
            </p>
            <p style={serif} className="mt-4 text-lg md:text-xl italic text-bone/70 leading-[1.9]">
              You are not simply names on a wall. You are part of the foundation on which
              StoicOs is being built.
            </p>
            <p style={serif} className="mt-8 text-base italic text-bone/55">With sincere gratitude,</p>
            <img
              src="/images/founder-ed.jpg"
              alt="Ed Korporaal, Founder of StoicOs"
              data-testid="founders-gratitude-photo"
              className="mt-6 mx-auto w-36 h-36 md:w-44 md:h-44 rounded-full object-cover border-2 border-gold/70 shadow-[0_0_35px_rgba(197,160,89,0.25)]"
              loading="lazy"
              draggable={false}
            />
            <p style={serif} className="mt-5 text-2xl text-gold-soft" data-testid="founders-gratitude-signature">Ed Korporaal</p>
            <p className="mt-1 text-[10px] tracking-[0.28em] uppercase text-bone/50">Founder of StoicOs</p>
          </div>
        </div>
      </div>
    </section>
  );
}
